// Aba de Configurações de Licitações: filtros (PNCP + ConLicitação), fonte
// PNCP, ConLicitação, triagem Dwight e A/B de modelos (palavras-chave, UFs,
// modalidades, janela, fit_score mínimo, valor mínimo, instruções customizadas).
// Extraído de ComercialLicitacoes.tsx em 2026-05-23 (frontend wave 3B).
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Tag, Form, Typography, Space,
  Alert, Checkbox, Slider, App as AntApp, Flex, InputNumber, Collapse, Popconfirm,
  Select, Table, Statistic, Row, Col, Divider, Tooltip, Tabs,
} from 'antd';
import { PlusOutlined, ApiOutlined, ClearOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { UFS, MODALIDADES_PADRAO } from './constants';
import { Se } from '../../components/Se';

const { Text } = Typography;

export function AbaConfig() {
  const { message } = AntApp.useApp();
  const [cfg, setCfg] = useState({
    base_url: 'https://pncp.gov.br/api/consulta/v1',
    palavras_chave: [] as string[],
    palavras_chave_negativas: [] as string[],
    ufs: [] as string[],
    modalidades: [4, 5, 6, 8] as number[],
    dias_para_frente: 60,
    ativo: false,
    ultima_sincronizacao: null as string | null,
    triagem_ia_ativa: true,
    instrucoes_dwight: '',
    fit_score_minimo: 40,
    auto_arquivar_dias: 7,
    valor_estimado_minimo: 50000,
    avisos_copia_emails: [] as string[],
  });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [novaPalavra, setNovaPalavra] = useState('');
  const [novaPalavraNeg, setNovaPalavraNeg] = useState('');
  // L11 (2026-06-02) — UI pra atualizar token ConLicitacao
  const [conlicToken, setConlicToken] = useState('');
  const [conlicSalvando, setConlicSalvando] = useState(false);
  const [conlicTestando, setConlicTestando] = useState(false);
  const [conlicStatus, setConlicStatus] = useState<{ tipo: 'success' | 'error' | 'info'; msg: string } | null>(null);
  // L13 (2026-06-02) — limpeza retroativa do backlog ConLic (~2800)
  const [limpando, setLimpando] = useState(false);
  // L15 (2026-06-03) — A/B test de modelos de triagem
  const [abModeloA, setAbModeloA] = useState('us.anthropic.claude-haiku-4-5-20251001-v1:0');
  const [abModeloB, setAbModeloB] = useState('us.amazon.nova-lite-v1:0');
  const [abN, setAbN] = useState(12);
  const [abRodando, setAbRodando] = useState(false);
  const [abResultado, setAbResultado] = useState<any>(null);
  // L15.1 (2026-06-03) — amostra estratificada (3 buckets)
  const [abEstratificada, setAbEstratificada] = useState(true);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  async function carregar() {
    setCarregando(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/licitacoes?action=config`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCfg(c => ({
        ...c, ...data,
        palavras_chave: Array.isArray(data.palavras_chave) ? data.palavras_chave : [],
        palavras_chave_negativas: Array.isArray(data.palavras_chave_negativas) ? data.palavras_chave_negativas : [],
        ufs: Array.isArray(data.ufs) ? data.ufs : [],
        modalidades: Array.isArray(data.modalidades) && data.modalidades.length ? data.modalidades : [4, 5, 6, 8],
        avisos_copia_emails: Array.isArray(data.avisos_copia_emails) ? data.avisos_copia_emails : [],
      }));
    } catch {}
    finally { setCarregando(false); }
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'salvar_config',
          palavras_chave: cfg.palavras_chave,
          palavras_chave_negativas: cfg.palavras_chave_negativas,
          ufs: cfg.ufs, modalidades: cfg.modalidades,
          dias_para_frente: cfg.dias_para_frente,
          // `ativo`: campo não é lido por nenhuma fonte; some da tela, não do banco.
          ativo: cfg.ativo,
          triagem_ia_ativa: cfg.triagem_ia_ativa,
          instrucoes_dwight: cfg.instrucoes_dwight,
          fit_score_minimo: cfg.fit_score_minimo,
          auto_arquivar_dias: cfg.auto_arquivar_dias,
          valor_estimado_minimo: cfg.valor_estimado_minimo,
          avisos_copia_emails: cfg.avisos_copia_emails,
        }, { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      // LIC-FIT: threshold subiu → arquiva retroativamente; desceu → restaura
      // as que tinham sido arquivadas por threshold e passam no novo valor
      if (d?.arquivadas_retro > 0) {
        message.success(
          `Configuração salva. Threshold ${d.fit_antigo}→${d.fit_novo}: `
          + `${d.arquivadas_retro} oportunidade(s) abaixo do novo fit foram arquivadas (reversível em "Mostrar arquivadas").`,
          8);
      } else if (d?.restauradas_retro > 0) {
        message.success(
          `Configuração salva. Threshold ${d.fit_antigo}→${d.fit_novo}: `
          + `${d.restauradas_retro} oportunidade(s) arquivadas por threshold voltaram pra "Em análise".`,
          8);
      } else {
        message.success('Configuração salva.');
      }
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  // L11 (2026-06-02) — salva o token do ConLicitacao (UUID X-AUTH-TOKEN)
  async function salvarTokenConlic() {
    // L11 fix: tira qualquer caractere nao-ASCII que tenha vindo no copy/paste
    // (zero-width space, ✗ decorativo do alert anterior, emojis, etc).
    // HTTP headers no ConLicitacao so aceitam ASCII puro.
    const tk = conlicToken.replace(/[^\x20-\x7E]/g, '').trim();
    if (!tk) { message.warning('Cole o token UUID recebido por email'); return; }
    if (tk !== conlicToken.trim()) {
      message.info('Removi caracteres invisíveis do token antes de salvar.');
    }
    setConlicSalvando(true);
    setConlicStatus(null);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'conlic_atualizar_token', token: tk },
        { headers: { Authorization: token } });
      message.success('Token salvo. Aguarde 5-10s antes de testar.');
      setConlicToken('');
      setConlicStatus({ tipo: 'success', msg: 'Token atualizado. Lambda em cold start (5-10s).' });
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      message.error('Erro: ' + msg);
      setConlicStatus({ tipo: 'error', msg });
    } finally { setConlicSalvando(false); }
  }

  // L15 (2026-06-03) — roda A/B test entre 2 modelos de triagem
  async function rodarAbTest() {
    setAbRodando(true);
    setAbResultado(null);
    try {
      const token = await getToken();
      // Timeout grande pra acomodar 30-60s (Bedrock 2N chamadas)
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'ab_test_triagem', modelo_a: abModeloA, modelo_b: abModeloB, n: abN, estratificada: abEstratificada },
        { headers: { Authorization: token }, timeout: 180000 });
      const d = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      setAbResultado(d);
      if (d?.erro) message.warning(d.erro);
      else message.success(`A/B rodou em ${d?.resumo?.tempo_total_ms || '?'}ms`);
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      message.error('Erro: ' + msg);
      setAbResultado({ erro: msg });
    } finally { setAbRodando(false); }
  }

  // L13 — limpeza retro do backlog ConLic (~2800 importados antes do filtro)
  async function limparBacklogConlic() {
    setLimpando(true);
    setConlicStatus(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'conlic_aplicar_filtros_retro' },
        { headers: { Authorization: token }, timeout: 120000 });
      const data = typeof r.data?.body === 'string' ? JSON.parse(r.data.body) : r.data;
      const m = data?.motivos || {};
      const msg = `${data?.arquivadas ?? '?'} arquivadas (palavras: ${m.keywords || 0}, valor: ${m.valor || 0}, vencidas: ${m.vencida || 0}) · ${data?.mantidas ?? '?'} mantidas`;
      setConlicStatus({ tipo: 'success', msg: `✓ Limpeza retro OK. ${msg}` });
      message.success(`Limpeza retroativa: ${data?.arquivadas || 0} arquivadas`);
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      setConlicStatus({ tipo: 'error', msg: `✗ ${msg}` });
      message.error('Erro na limpeza: ' + msg);
    } finally { setLimpando(false); }
  }

  // L11 — testa conexao usando o token atual
  async function testarConlic() {
    setConlicTestando(true);
    setConlicStatus(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'conlic_testar_conexao' },
        { headers: { Authorization: token }, timeout: 30000 });
      // L11 fix #2 (2026-06-02): o testar_conexao retorna {ok, status,
      // ip_visto_pelo_conlicitacao, body} — onde "body" eh um CAMPO de
      // negocio (a resposta crua do ConLicitacao), nao o body raw do API
      // Gateway. So tenta JSON.parse se realmente vier string.
      const data = typeof r.data?.body === 'string'
        ? JSON.parse(r.data.body)
        : r.data;
      const sucesso = data?.ok === true || data?.sucesso === true;
      const httpStatus = data?.status;
      const ipVisto = data?.ip_visto_pelo_conlicitacao;
      const bodyResumo = typeof data?.body === 'string'
        ? data.body.slice(0, 200)
        : JSON.stringify(data?.body || {}).slice(0, 200);
      const partes = [
        httpStatus ? `HTTP ${httpStatus}` : null,
        ipVisto ? `IP visto: ${ipVisto}` : null,
        bodyResumo ? `Resposta: ${bodyResumo}` : null,
      ].filter(Boolean).join(' · ');
      if (sucesso) {
        setConlicStatus({ tipo: 'success', msg: `✓ Conexão OK. ${partes}` });
        message.success('ConLicitação respondeu OK');
      } else {
        setConlicStatus({ tipo: 'error', msg: `✗ ${partes || JSON.stringify(data).slice(0, 300)}` });
      }
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message;
      setConlicStatus({ tipo: 'error', msg: `✗ ${msg}` });
      message.error('Erro: ' + msg);
    } finally { setConlicTestando(false); }
  }

  async function testar() {
    setTestando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'testar_config' }, { headers: { Authorization: token }, timeout: 30000 });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const linhas = [
        `✓ ${data.fonte} respondeu OK`, '',
        `Janela: ${data.janela}`,
        `Pregões publicados: ${data.total_disponivel_amostra} (em ${data.total_paginas} páginas)`,
        `Com proposta aberta: ${data.ainda_abertas}`, '',
        `Amostra dos 3 primeiros:`,
      ];
      (data.preview || []).forEach((p: any, i: number) => {
        linhas.push(`${i + 1}. [${p.uf}] ${p.orgao}`);
        linhas.push(`   ${p.objeto}`);
        linhas.push(`   Valor: R$ ${(p.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
      });
      linhas.push('', data.mensagem);
      alert(linhas.join('\n'));
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setTestando(false); }
  }

  function adicionarPalavra() {
    const p = novaPalavra.trim().toLowerCase();
    if (!p) return;
    if (cfg.palavras_chave.includes(p)) { setNovaPalavra(''); return; }
    setCfg({ ...cfg, palavras_chave: [...cfg.palavras_chave, p] });
    setNovaPalavra('');
  }
  function removerPalavra(p: string) {
    setCfg({ ...cfg, palavras_chave: cfg.palavras_chave.filter(x => x !== p) });
  }
  function adicionarPalavraNeg() {
    const p = novaPalavraNeg.trim().toLowerCase();
    if (!p) return;
    if (cfg.palavras_chave_negativas.includes(p)) { setNovaPalavraNeg(''); return; }
    setCfg({ ...cfg, palavras_chave_negativas: [...cfg.palavras_chave_negativas, p] });
    setNovaPalavraNeg('');
  }
  function removerPalavraNeg(p: string) {
    setCfg({ ...cfg, palavras_chave_negativas: cfg.palavras_chave_negativas.filter(x => x !== p) });
  }
  function toggleUf(uf: string) {
    setCfg({ ...cfg, ufs: cfg.ufs.includes(uf)
      ? cfg.ufs.filter(x => x !== uf) : [...cfg.ufs, uf] });
  }
  function toggleModalidade(id: number) {
    setCfg({ ...cfg, modalidades: cfg.modalidades.includes(id)
      ? cfg.modalidades.filter(x => x !== id) : [...cfg.modalidades, id] });
  }

  if (carregando) return <Card loading />;

  const sugestoes = ['topografia', 'batimetria', 'hidrologia', 'hidrometria', 'hidráulica',
    'sedimento', 'georreferenciamento', 'cadastro técnico', 'levantamento',
    'monitoramento de bacias'];

  // LIC-FIT (2026-06-09): página reorganizada em abas — antes era uma coluna
  // única que misturava PNCP, triagem IA, ConLicitação e A/B test sem
  // separação clara. Desde 2026-09-02 são 5: Filtros (o que vale para as DUAS
  // fontes), PNCP, ConLicitação (voltou em 01/09/2026), Triagem IA e A/B.
  // Cada aba diz de quem é cada campo — a régua é quem LÊ o campo no backend.
  const botaoSalvar = (
    <Flex gap={8} justify="flex-end" style={{ marginTop: 14 }}>
      <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
    </Flex>
  );
  const botoesSalvarPncp = (
    <Flex gap={8} justify="flex-end" style={{ marginTop: 14 }}>
      <Button icon={<ApiOutlined />} loading={testando} onClick={testar}
        style={{ color: '#B07500', borderColor: '#B07500' }}>
        Testar conexão PNCP
      </Button>
      <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
    </Flex>
  );

  const alertaFontePncp = (
    <Alert
      type="info" showIcon style={{ marginBottom: 18 }}
      message={<Text strong style={{ color: '#020D7F' }}>Fonte: PNCP (Portal Nacional de Contratações Públicas)</Text>}
      description={
        <div>
          <Text style={{ fontSize: 11 }}>
            API oficial e gratuita do governo federal. Não precisa token nem assinatura.
          </Text>
          {cfg.ultima_sincronizacao && (
            <div style={{ marginTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Última sincronização (PNCP ou ConLicitação): {new Date(cfg.ultima_sincronizacao).toLocaleString('pt-BR')}
              </Text>
            </div>
          )}
        </div>
      }
    />
  );

  const tabFiltros = (
    <>
      <Card size="small" title="Palavras-chave para filtrar editais relevantes" style={{ marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 6 }}>
          Valem para as duas fontes (PNCP e ConLicitação): busca por palavra inteira no objeto, sem diferenciar
          acento nem plural. Sugestões:
        </Text>
        <Space wrap size={4} style={{ marginBottom: 8 }}>
          {sugestoes.map(s => (
            <Tag key={s} color="blue" style={{ cursor: 'pointer' }}
              onClick={() => {
                if (!cfg.palavras_chave.includes(s)) {
                  setCfg({ ...cfg, palavras_chave: [...cfg.palavras_chave, s] });
                }
              }}>+ {s}</Tag>
          ))}
        </Space>
        <Space.Compact style={{ width: '100%', marginBottom: 6 }}>
          <Input value={novaPalavra} onChange={e => setNovaPalavra(e.target.value)}
            onPressEnter={adicionarPalavra} placeholder="Ex.: levantamento batimétrico" />
          <Button type="primary" icon={<PlusOutlined />} onClick={adicionarPalavra}>Adicionar</Button>
        </Space.Compact>
        <Space wrap size={4} style={{ minHeight: 28 }}>
          {cfg.palavras_chave.length === 0
            ? <Text type="secondary" italic style={{ fontSize: 11 }}>Nenhuma palavra-chave configurada.</Text>
            : cfg.palavras_chave.map(p => (
              <Tag key={p} closable color="blue" onClose={() => removerPalavra(p)}>{p}</Tag>
            ))}
        </Space>
      </Card>

      <Card size="small" title="🚫 Palavras-chave NEGATIVAS (blacklist)" style={{ marginBottom: 14 }}>
        <Alert type="info" style={{ marginBottom: 10, fontSize: 11 }}
          message="Se o objeto contém qualquer um destes termos, a oportunidade é arquivada antes da triagem por IA (não é apagada). PNCP e ConLicitação." />
        <Space.Compact style={{ width: '100%', marginBottom: 6 }}>
          <Input value={novaPalavraNeg} onChange={e => setNovaPalavraNeg(e.target.value)}
            onPressEnter={adicionarPalavraNeg} placeholder="Ex.: alimentação, papelaria, uniforme" />
          <Button type="primary" danger icon={<PlusOutlined />} onClick={adicionarPalavraNeg}>Adicionar</Button>
        </Space.Compact>
        <Space wrap size={4} style={{ minHeight: 28 }}>
          {cfg.palavras_chave_negativas.length === 0
            ? <Text type="secondary" italic style={{ fontSize: 11 }}>Nenhuma palavra negativa configurada.</Text>
            : cfg.palavras_chave_negativas.map(p => (
              <Tag key={p} closable color="red" onClose={() => removerPalavraNeg(p)}>{p}</Tag>
            ))}
        </Space>
      </Card>

      <Card size="small" title="Valor estimado mínimo do edital" style={{ marginBottom: 14 }}>
        <Flex align="center" gap={12} wrap>
          <InputNumber
            min={0}
            step={5000}
            value={cfg.valor_estimado_minimo}
            onChange={(v) => setCfg({ ...cfg, valor_estimado_minimo: Number(v) || 0 })}
            style={{ width: 200 }}
            formatter={(v) => `R$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            parser={(v: string | undefined) => Number((v || '').replace(/\D/g, ''))}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Edital abaixo disso é arquivado antes da triagem por IA. PNCP e ConLicitação. 0 = sem filtro.
          </Text>
        </Flex>
      </Card>
      {botaoSalvar}
    </>
  );

  const tabPncp = (
    <>
      {alertaFontePncp}

      <Card size="small" title="UFs preferenciais" style={{ marginBottom: 14 }}
        extra={cfg.ufs.length > 0
          ? <Button size="small" type="link" danger onClick={() => setCfg({ ...cfg, ufs: [] })}>limpar</Button>
          : null}>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 6 }}>
          Só o PNCP filtra por UF. O ConLicitação traz o que o filtro cadastrado no portal deles define. Vazio = todas.
        </Text>
        <Space wrap size={4}>
          {UFS.map(uf => (
            <Tag.CheckableTag key={uf} checked={cfg.ufs.includes(uf)} onChange={() => toggleUf(uf)}>{uf}</Tag.CheckableTag>
          ))}
        </Space>
      </Card>

      <Card size="small" title="Modalidades de contratação" style={{ marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 6 }}>
          Só PNCP. Quais modalidades sincronizar. Recomendado: pregão eletrônico + concorrência.
        </Text>
        <Space direction="vertical">
          {MODALIDADES_PADRAO.map(m => (
            <Checkbox key={m.id} checked={cfg.modalidades.includes(m.id)} onChange={() => toggleModalidade(m.id)}>
              <Text style={{ fontSize: 12 }}>
                {m.label}
                <Text type="secondary" style={{ fontSize: 10, marginLeft: 6 }}>(código {m.id})</Text>
                {m.dest && <Text style={{ fontSize: 10, color: '#0a7f4a', marginLeft: 6 }}>★ recomendada</Text>}
              </Text>
            </Checkbox>
          ))}
        </Space>
      </Card>

      <Card size="small" title="Janela de publicação no PNCP" style={{ marginBottom: 14 }}>
        <Flex align="center" gap={12}>
          <Slider min={1} max={30} step={1} value={cfg.dias_para_frente}
            onChange={(v) => setCfg({ ...cfg, dias_para_frente: v })} style={{ flex: 1 }} />
          <Text strong style={{ color: '#0930D0', minWidth: 80 }}>{cfg.dias_para_frente} dia(s)</Text>
        </Flex>
        <Text type="secondary" style={{ fontSize: 10 }}>
          Só PNCP: editais publicados nos últimos N dias, com proposta aberta. 7 dias = sync semanal.
        </Text>
      </Card>
      {botoesSalvarPncp}
    </>
  );

  const tabTriagem = (
    <>
      <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>🤖 Triagem inteligente (Dwight + Bedrock)</Text>}
        style={{ marginBottom: 14, background: '#FAFBFF' }}>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
          Dwight analisa cada licitação importada usando Claude. Avalia o objeto, atribui fit técnico (0-100),
          área técnica e decide entre "em análise" ou "descartado".
        </Text>

        <Collapse
          size="small" ghost style={{ marginBottom: 12 }}
          items={[{
            key: 'r', label: <Text strong style={{ color: '#0930D0' }}>📏 Como o Dwight calcula o fit técnico</Text>,
            children: (
              <div style={{ fontSize: 11, lineHeight: 1.6 }}>
                <Text strong>Régua aplicada:</Text>
                <ul style={{ paddingLeft: 18, marginTop: 4 }}>
                  <li><strong>80-100 · Alto:</strong> objeto no core das áreas técnicas da Hydrodata.</li>
                  <li><strong>40-79 · Médio:</strong> aderência parcial, com componentes fora do escopo.</li>
                  <li><strong>0-39 · Baixo:</strong> objeto predominantemente fora do escopo.</li>
                </ul>
                <Text>Localização, valor, prazo e pendências de habilitação são alertas separados. O checklist mede preparação documental e não reduz o fit técnico. Participar continua sendo uma decisão da equipe.</Text>
              </div>
            ),
          }]}
        />

        <Checkbox checked={cfg.triagem_ia_ativa} onChange={e => setCfg({ ...cfg, triagem_ia_ativa: e.target.checked })}>
          <Text strong>Triagem por IA ativa</Text>
        </Checkbox>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
          O disparo automático acontece ao fim de cada sync (PNCP e ConLicitação); a fila inclui oportunidades de
          qualquer fonte em 'Novo'. Desmarcar segura só o disparo do sync do PNCP — o do ConLicitação e o botão
          'Triar Dwight' na lista rodam a mesma fila mesmo assim.
        </Text>

        <Form.Item label="Fit_score mínimo (arquiva abaixo disso)" style={{ marginTop: 12, marginBottom: 12 }}>
          <Flex align="center" gap={12}>
            <Slider min={0} max={100} step={5} value={cfg.fit_score_minimo}
              onChange={(v) => setCfg({ ...cfg, fit_score_minimo: v })} style={{ flex: 1 }} />
            <Text strong style={{ color: '#0930D0', minWidth: 60 }}>{cfg.fit_score_minimo}/100</Text>
          </Flex>
          <Text type="secondary" style={{ fontSize: 10 }}>
            Editais com fit abaixo desse valor são arquivados com a nota e o parecer gravados (status Descartado).
            Aparecem em 'Mostrar arquivadas' e podem ser desarquivados.
          </Text>
        </Form.Item>

        <Form.Item label="Instruções customizadas para o Dwight (régua extra)">
          <Input.TextArea value={cfg.instrucoes_dwight}
            onChange={e => setCfg({ ...cfg, instrucoes_dwight: e.target.value })}
            rows={6}
            placeholder={'Ex.:\n- Foco em RS/SC/PR; outras UFs só se valor > R$ 500k\n- Evitar contratos com prazo < 30 dias\n- Descartar imediatamente qualquer obra civil'} />
        </Form.Item>

        <Alert type="warning" showIcon style={{ marginTop: 8 }}
          message={
            <Text style={{ fontSize: 11 }}>
              A fonte primária de conhecimento está em <strong>Comercial → Dwight → Configuração</strong>,
              campo "descricao_empresa". Mantenha-a atualizada.
            </Text>
          } />
      </Card>

      {/* Pipeline de licitações (02/10/2026): os avisos de prazo (7, 3, 1 dia e no dia)
          vão ao responsável pela licitação E a esta lista. Decisão do Lucas: ele e o
          Gustavo. Sem responsável a licitação nem entra no quadro. */}
      <Card size="small" title="⏰ Avisos de prazo do pipeline — quem recebe cópia" style={{ marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          Todo dia às 8h o sistema avisa o responsável de cada licitação do pipeline quando o prazo da
          proposta está a 7, 3 e 1 dia — e no dia. Estes e-mails recebem cópia de todos os avisos.
        </Text>
        <Select mode="tags" value={cfg.avisos_copia_emails} style={{ width: '100%' }}
          tokenSeparators={[',', ';', ' ']} placeholder="e-mail@hydrodataeng.com.br"
          onChange={(v) => setCfg({ ...cfg, avisos_copia_emails: (v as string[]).map(s => s.trim().toLowerCase()).filter(s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) })}
          options={cfg.avisos_copia_emails.map(e => ({ value: e, label: e }))} />
      </Card>

      <Card size="small" title="📦 Limpeza automática" style={{ marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
          Roda junto com o sync do PNCP; arquiva descartadas de qualquer fonte há mais de N dias.
        </Text>
        <Flex align="center" gap={12}>
          <Slider min={0} max={30} step={1} value={cfg.auto_arquivar_dias}
            onChange={(v) => setCfg({ ...cfg, auto_arquivar_dias: v })} style={{ flex: 1 }} />
          <Text strong style={{ color: '#0930D0', minWidth: 90 }}>
            {cfg.auto_arquivar_dias === 0 ? 'desativado' : `${cfg.auto_arquivar_dias} dias`}
          </Text>
        </Flex>
      </Card>
      {botaoSalvar}
    </>
  );

  // ConLicitação voltou em 01/09/2026 (o fornecedor ativou a API). Token e
  // limpeza retroativa exigem admin.permissoes.editar no backend — o <Se>
  // esconde o que a pessoa não conseguiria salvar de qualquer jeito.
  const tabConlic = (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 14, fontSize: 12 }}
        message="Fonte paga, ativa desde 01/09/2026 (assinatura anual com API)"
        description="Sincroniza 3 vezes por dia (10h00, 13h15 e 18h45). Palavras-chave, negativas e valor mínimo da aba Filtros valem aqui também; UF e modalidade vêm do filtro cadastrado no portal do ConLicitação. Os links de documento do boletim expiram em 24 h." />
      <Se permissao="admin.permissoes.editar"
        fallback={<Alert type="info" showIcon message="Só administradores alteram o token e a limpeza retroativa." />}>
      {/* L11 (2026-06-02) — ConLicitacao (API paga, fonte complementar) */}
      <Card size="small" title="ConLicitação — token X-AUTH-TOKEN">
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
          message="Fonte paga, complementar ao PNCP gratuito"
          description={
            <>
              <div>O token UUID chega por email do 0800 11 14133. Cole abaixo e clique <strong>Salvar token</strong>. A Lambda re-inicializa em 5-10s pra limpar o cache antigo, então clique <strong>Testar conexão</strong> depois pra confirmar.</div>
              <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>O sistema sai pela Internet com IP fixo <code>98.90.130.111</code> (já cadastrado no ConLicitação).</div>
            </>
          } />
        <Form layout="vertical" size="small">
          <Form.Item label="Novo token (formato UUID)" style={{ marginBottom: 8 }}>
            <Input.Password value={conlicToken}
              onChange={e => setConlicToken(e.target.value)}
              placeholder="ex.: 00000000-0000-0000-0000-000000000000"
              autoComplete="off" />
          </Form.Item>
        </Form>
        {conlicStatus && (
          <Alert type={conlicStatus.tipo} showIcon style={{ marginBottom: 10, fontSize: 12 }}
            message={conlicStatus.msg} />
        )}
        <Flex gap={8} justify="flex-end">
          <Button icon={<ApiOutlined />} loading={conlicTestando} onClick={testarConlic}
            style={{ color: '#7C3AED', borderColor: '#7C3AED' }}>
            Testar conexão
          </Button>
          <Button type="primary" loading={conlicSalvando} onClick={salvarTokenConlic}
            disabled={!conlicToken.trim()}>
            Salvar token
          </Button>
        </Flex>
        {/* L13 (2026-06-02) — limpeza retro do backlog importado antes dos filtros */}
        <Alert type="warning" showIcon style={{ marginTop: 16, fontSize: 11 }}
          message="Limpeza retroativa do backlog"
          description="Aplica os filtros atuais (whitelist, blacklist, valor mínimo, vencidas) nas oportunidades ConLic já no banco que ainda estão em 'Novo' ou 'Em análise' e sem proposta vinculada. Arquiva (soft delete) — reversível. Use depois de calibrar as palavras-chave e o valor mínimo na aba Filtros." />
        <Flex justify="flex-end" style={{ marginTop: 8 }}>
          <Popconfirm
            title="Aplicar filtros retroativamente?"
            description="Vai arquivar oportunidades que não passam nos filtros atuais. Reversível (status 'descartado' com arquivada_em)."
            okText="Sim, limpar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={limparBacklogConlic}>
            <Button icon={<ClearOutlined />} danger loading={limpando}>
              Limpar backlog (aplicar filtros retro)
            </Button>
          </Popconfirm>
        </Flex>
      </Card>
      </Se>
    </>
  );

  const tabAb = (
      /* L15 (2026-06-03) — Teste A/B de modelo de triagem */
      <Card size="small"
        title={<Space><ExperimentOutlined style={{ color: '#7C3AED' }} /><span>Teste A/B de modelo (triagem)</span></Space>}>
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
          message="Comparar 2 modelos lado a lado, sem alterar nada no banco"
          description={
            <>
              <div>Pega N licitações não-triadas (status 'novo'), roda os 2 modelos em PARALELO sobre o mesmo conjunto, mostra:</div>
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                <li>Concordância na decisão (participar/descartar)</li>
                <li>Diferença média no fit_score</li>
                <li>Custo total estimado (USD)</li>
                <li>Latência média</li>
                <li>Linha-a-linha pra você inspecionar divergências</li>
              </ul>
              <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                Demora ~30-60s. Não persiste nada — pode rodar quantas vezes quiser. Use pra decidir se troca o modelo default da triagem.
              </div>
            </>
          } />
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={24} md={10}>
            <Form.Item label="Modelo A (atual)" style={{ marginBottom: 0 }}>
              <Select value={abModeloA} onChange={setAbModeloA} size="small"
                options={[
                  { label: 'Claude Haiku 4.5 ($0.80/$4 per Mtok)', value: 'us.anthropic.claude-haiku-4-5-20251001-v1:0' },
                  { label: 'Claude Sonnet 4.5 ($3/$15)', value: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0' },
                  { label: 'Amazon Nova Lite ($0.06/$0.24)', value: 'us.amazon.nova-lite-v1:0' },
                  { label: 'Amazon Nova Micro ($0.035/$0.14)', value: 'us.amazon.nova-micro-v1:0' },
                  { label: 'Amazon Nova Pro ($0.80/$3.20)', value: 'us.amazon.nova-pro-v1:0' },
                  { label: 'Llama 3.3 70B ($0.72/$0.72)', value: 'us.meta.llama3-3-70b-instruct-v1:0' },
                ]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="Modelo B (alternativo)" style={{ marginBottom: 0 }}>
              <Select value={abModeloB} onChange={setAbModeloB} size="small"
                options={[
                  { label: 'Amazon Nova Lite ($0.06/$0.24)', value: 'us.amazon.nova-lite-v1:0' },
                  { label: 'Amazon Nova Micro ($0.035/$0.14)', value: 'us.amazon.nova-micro-v1:0' },
                  { label: 'Amazon Nova Pro ($0.80/$3.20)', value: 'us.amazon.nova-pro-v1:0' },
                  { label: 'Claude Haiku 4.5 ($0.80/$4)', value: 'us.anthropic.claude-haiku-4-5-20251001-v1:0' },
                  { label: 'Claude Sonnet 4.5 ($3/$15)', value: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0' },
                  { label: 'Llama 3.3 70B ($0.72/$0.72)', value: 'us.meta.llama3-3-70b-instruct-v1:0' },
                ]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item label="N" style={{ marginBottom: 0 }}>
              <InputNumber size="small" value={abN} onChange={(v) => setAbN(Number(v) || 10)} min={3} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Flex justify="space-between" align="center">
          <Checkbox checked={abEstratificada} onChange={e => setAbEstratificada(e.target.checked)}>
            <Tooltip title="Pega N/3 não-triadas + N/3 borderline (Dwight fit 40-65) + N/3 alto fit (Dwight ≥70). Compara A vs B vs decisão original do Dwight.">
              <Text style={{ fontSize: 12 }}>
                <strong>Amostra estratificada</strong> — 3 buckets (novas, borderline, alto fit)
              </Text>
            </Tooltip>
          </Checkbox>
          <Button type="primary" icon={<ExperimentOutlined />} loading={abRodando} onClick={rodarAbTest}
            disabled={abModeloA === abModeloB}
            style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
            Rodar A/B ({abN} licitações)
          </Button>
        </Flex>
        {abResultado && !abResultado.erro && abResultado.resumo && (
          <>
            <Divider style={{ margin: '14px 0' }} />
            <Row gutter={12}>
              <Col xs={12} md={6}>
                <Statistic title="Concordância da decisão"
                  value={abResultado.resumo.concordancia_decisao_pct}
                  suffix="%" precision={1}
                  valueStyle={{ color: abResultado.resumo.concordancia_decisao_pct >= 85 ? '#0a7f4a' : '#B07500' }} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Diff médio fit_score"
                  value={abResultado.resumo.diff_medio_fit}
                  precision={1} suffix="pts"
                  valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Custo A (USD)"
                  value={abResultado.resumo.a.custo_total_usd}
                  precision={4} prefix="$" valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title={`Custo B (USD)${abResultado.resumo.economia_b_vs_a_pct != null ? ' · economia ' + abResultado.resumo.economia_b_vs_a_pct + '%' : ''}`}
                  value={abResultado.resumo.b.custo_total_usd}
                  precision={4} prefix="$"
                  valueStyle={{ fontSize: 18, color: abResultado.resumo.economia_b_vs_a_pct > 0 ? '#0a7f4a' : '#B91626' }} />
              </Col>
            </Row>
            <Row gutter={12} style={{ marginTop: 8 }}>
              <Col xs={24} md={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  A: {abResultado.resumo.a.modelo.split('.').slice(-1)[0]} · latência {abResultado.resumo.a.latencia_media_ms}ms · {abResultado.resumo.a.tokens_in_total} tok in / {abResultado.resumo.a.tokens_out_total} tok out
                </Text>
              </Col>
              <Col xs={24} md={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  B: {abResultado.resumo.b.modelo.split('.').slice(-1)[0]} · latência {abResultado.resumo.b.latencia_media_ms}ms · {abResultado.resumo.b.tokens_in_total} tok in / {abResultado.resumo.b.tokens_out_total} tok out
                </Text>
              </Col>
            </Row>
            {/* L15.1 — resumo por bucket (so quando estratificada) */}
            {abResultado.resumo.por_bucket && Object.keys(abResultado.resumo.por_bucket).length > 0 && (
              <>
                <Divider style={{ margin: '14px 0 8px 0' }} />
                <Text strong style={{ fontSize: 12, color: '#020D7F' }}>Por bucket</Text>
                <Row gutter={8} style={{ marginTop: 4 }}>
                  {Object.entries(abResultado.resumo.por_bucket).map(([nome, st]: [string, any]) => {
                    const cor = nome === 'novo' ? '#0930D0' : nome === 'borderline' ? '#B07500' : '#0a7f4a';
                    const label = nome === 'novo' ? 'Não-triadas' : nome === 'borderline' ? 'Borderline (fit 40-65)' : 'Alto fit (≥70)';
                    return (
                      <Col xs={24} md={8} key={nome}>
                        <Card size="small" style={{ borderTop: `3px solid ${cor}` }} styles={{ body: { padding: 10 } }}>
                          <Text strong style={{ color: cor, fontSize: 11 }}>{label}</Text>
                          <div style={{ fontSize: 10, marginTop: 4 }}>
                            <Text type="secondary">N: {st.total}</Text>
                          </div>
                          <div style={{ fontSize: 10 }}>
                            <Text type="secondary">Concord. A=B: <strong>{st.concordancia_ab_pct}%</strong></Text>
                          </div>
                          {st.a_bate_dwight_pct != null && (
                            <div style={{ fontSize: 10 }}>
                              <Text type="secondary">A bate Dwight: <strong>{st.a_bate_dwight_pct}%</strong></Text>
                            </div>
                          )}
                          {st.b_bate_dwight_pct != null && (
                            <div style={{ fontSize: 10 }}>
                              <Text type="secondary">B bate Dwight: <strong>{st.b_bate_dwight_pct}%</strong></Text>
                            </div>
                          )}
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}
            <Divider style={{ margin: '14px 0' }} />
            <Table
              size="small"
              dataSource={abResultado.itens}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1100 }}
              columns={[
                { title: 'Bucket', key: 'bk', width: 95, render: (_: any, r: any) => {
                  const cor = r.bucket === 'novo' ? 'blue' : r.bucket === 'borderline' ? 'orange' : 'green';
                  const lbl = r.bucket === 'novo' ? 'novo' : r.bucket === 'borderline' ? 'border' : 'alto fit';
                  return <Tag color={cor} style={{ fontSize: 9 }}>{lbl}</Tag>;
                } },
                { title: 'Edital', dataIndex: 'numero_edital', key: 'ed', width: 140, render: (v: string, r: any) => (
                  <Tooltip title={r.objeto_resumo}>
                    <div>
                      <Text strong style={{ fontSize: 11 }}>{v || '—'}</Text>
                      <div><Text type="secondary" style={{ fontSize: 9 }}>{r.orgao}/{r.uf}</Text></div>
                    </div>
                  </Tooltip>
                ) },
                { title: 'Dwight orig.', key: 'dw', width: 110, render: (_: any, r: any) => {
                  if (!r.dwight_original) return <Text type="secondary" style={{ fontSize: 10 }}>—</Text>;
                  return (
                    <div>
                      <Tag color={r.dwight_original.decisao === 'participar' ? 'green' : 'red'} style={{ fontSize: 9, marginBottom: 2 }}>
                        {r.dwight_original.decisao}
                      </Tag>
                      <div><Text style={{ fontSize: 10 }}>fit {r.dwight_original.fit ?? '—'}</Text></div>
                    </div>
                  );
                } },
                { title: 'A: decisão', key: 'ad', width: 90, render: (_: any, r: any) => r.a?.sucesso
                  ? <div>
                      <Tag color={r.a.decisao === 'participar' ? 'green' : 'red'}>{r.a.decisao}</Tag>
                      {r.a_bate_dwight != null && (
                        <div><Text style={{ fontSize: 9, color: r.a_bate_dwight ? '#0a7f4a' : '#B91626' }}>
                          {r.a_bate_dwight ? '✓ bate Dwight' : '✗ diverge'}
                        </Text></div>
                      )}
                    </div>
                  : <Tag color="default">erro</Tag> },
                { title: 'A: fit', key: 'af', width: 55, render: (_: any, r: any) => r.a?.fit_score ?? '—' },
                { title: 'B: decisão', key: 'bd', width: 90, render: (_: any, r: any) => r.b?.sucesso
                  ? <div>
                      <Tag color={r.b.decisao === 'participar' ? 'green' : 'red'}>{r.b.decisao}</Tag>
                      {r.b_bate_dwight != null && (
                        <div><Text style={{ fontSize: 9, color: r.b_bate_dwight ? '#0a7f4a' : '#B91626' }}>
                          {r.b_bate_dwight ? '✓ bate Dwight' : '✗ diverge'}
                        </Text></div>
                      )}
                    </div>
                  : <Tag color="default">erro</Tag> },
                { title: 'B: fit', key: 'bf', width: 55, render: (_: any, r: any) => r.b?.fit_score ?? '—' },
                { title: 'Diff A→B', key: 'df', width: 70, render: (_: any, r: any) => r.diff_fit != null
                  ? <Text style={{ color: Math.abs(r.diff_fit) > 20 ? '#B91626' : Math.abs(r.diff_fit) > 10 ? '#B07500' : '#0a7f4a' }}>
                      {r.diff_fit > 0 ? '+' : ''}{r.diff_fit}
                    </Text> : '—' },
                { title: 'A≠B?', key: 'dv', width: 70, render: (_: any, r: any) => r.divergiu_decisao === true
                  ? <Tag color="red">SIM</Tag>
                  : r.divergiu_decisao === false ? <Tag color="green">não</Tag>
                  : <Tag color="default">n/d</Tag> },
              ] as ColumnsType<any>}
            />
          </>
        )}
        {abResultado?.erro && (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={abResultado.erro} />
        )}
      </Card>
  );

  return (
    <div style={{ maxWidth: 820 }}>
      <Tabs size="small" defaultActiveKey="filtros"
        items={[
          { key: 'filtros',      label: '🔎 Filtros (PNCP + ConLicitação)', children: tabFiltros },
          { key: 'pncp',         label: '🌐 PNCP',                          children: tabPncp },
          { key: 'conlicitacao', label: '🟣 ConLicitação',                  children: tabConlic },
          { key: 'triagem',      label: '🤖 Triagem IA',                    children: tabTriagem },
          { key: 'ab',           label: '🧪 A/B de modelos',                children: tabAb },
        ]} />
    </div>
  );
}
